/**
 * Pick a bake folder from disk — the DOM half of the library read (phase 5).
 *
 * `showDirectoryPicker` where the browser has it (Chromium), walked two
 * levels deep (the root's `manifest.json` / `sets.json` and the `<group>/`
 * clip folders — ADR-178's layout); a `<input type=file webkitdirectory>`
 * everywhere else, whose Files carry `webkitRelativePath`. Both yield the
 * same file-likes `bakeFolderFromFiles` indexes. Taken at the GESTURE (the
 * #140 rule): the picker is asked before any work.
 *
 * DOM only, no engine. Depends on: nothing. Depended on by: main.
 */
import type { FolderFileLike } from './library-read.js';

interface DirHandle {
  readonly name: string;
  values(): AsyncIterable<{ kind: 'file' | 'directory'; name: string; getFile?: () => Promise<File> } & Partial<DirHandle>>;
}
interface PickerWindow {
  showDirectoryPicker?: (options?: { mode?: 'read' }) => Promise<DirHandle>;
}

export interface PickedFolder {
  readonly name: string;
  readonly files: readonly FolderFileLike[];
}

/** A dropped/picked File as the folder seam sees it. */
export function fileLike(file: File, path: string): FolderFileLike {
  return { name: file.name, path, text: () => file.text(), arrayBuffer: () => file.arrayBuffer() };
}

async function walk(dir: DirHandle, prefix: string, depth: number, out: FolderFileLike[]): Promise<void> {
  for await (const entry of dir.values()) {
    const path = `${prefix}${entry.name}`;
    if (entry.kind === 'file' && entry.getFile !== undefined) {
      const file = await entry.getFile();
      out.push(fileLike(file, path));
    } else if (entry.kind === 'directory' && depth > 0 && entry.values !== undefined) {
      await walk(entry as DirHandle, `${path}/`, depth - 1, out);
    }
  }
}

/** null = cancelled (a cancel is not a folder — the caller must not report one). */
export async function pickBakeFolder(): Promise<PickedFolder | null> {
  const picker = (window as PickerWindow).showDirectoryPicker;
  if (picker !== undefined) {
    let dir: DirHandle;
    try {
      dir = await picker({ mode: 'read' });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return null;
      throw err;
    }
    const files: FolderFileLike[] = [];
    await walk(dir, `${dir.name}/`, 1, files);
    return { name: dir.name, files };
  }
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.setAttribute('webkitdirectory', '');
    input.multiple = true;
    input.style.display = 'none';
    input.addEventListener('change', () => {
      const list = input.files === null ? [] : Array.from(input.files);
      input.remove();
      if (list.length === 0) {
        resolve(null);
        return;
      }
      const rel = (f: File): string => (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
      const name = rel(list[0]!).split('/')[0] ?? 'bake';
      resolve({ name, files: list.map((f) => fileLike(f, rel(f))) });
    });
    input.addEventListener('cancel', () => {
      input.remove();
      resolve(null);
    });
    document.body.append(input);
    input.click();
  });
}
